// Formats an ISO datetime string as "Wed, 03/18/2026, 02:15 PM PDT" in the
// user's local timezone. Returns "" for missing or invalid input.
export const formatDateTime = (dateStr?: string): string => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    weekday: 'short',
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });
};

// kindLabel maps the unified BankTransaction.kind enum to a human-readable
// label. The vocabulary mirrors Mercury's TransactionKind enum (snake_cased).
export const kindLabel = (kind?: string): string => {
  switch (kind) {
    case 'external_transfer': return 'External Transfer';
    case 'internal_transfer': return 'Internal Transfer';
    case 'outgoing_payment': return 'Outgoing Payment';
    case 'credit_card_credit': return 'Credit Card Credit';
    case 'credit_card_transaction': return 'Credit Card';
    case 'debit_card_credit': return 'Debit Card Credit';
    case 'debit_card_transaction': return 'Debit Card';
    case 'card_international_transaction_fee': return 'Card International Fee';
    case 'card_international_transaction_fee_rebate': return 'Card International Fee Rebate';
    case 'card_international_transaction_fee_reversal': return 'Card International Fee Reversal';
    case 'card_international_transaction_fee_rebate_reversal': return 'Card International Fee Rebate Reversal';
    case 'incoming_domestic_wire': return 'Domestic Wire';
    case 'check_deposit': return 'Check Deposit';
    case 'incoming_international_wire': return 'International Wire';
    case 'treasury_transfer': return 'Treasury Transfer';
    case 'currency_cloud_return': return 'Currency Cloud Return';
    case 'wire_fee': return 'Wire Fee';
    case 'personal_banking_subscription_fee': return 'Personal Banking Subscription Fee';
    case 'billing_engine_subscription_fee': return 'Billing Engine Subscription Fee';
    case 'expense_reimbursement': return 'Expense Reimbursement';
    case 'exogenous_wire_drawdown': return 'Exogenous Wire Drawdown';
    case 'other': return 'Other';
    default: return kind || '';
  }
};

export const kindBadgeVariant = (kind?: string): string => {
  switch (kind) {
    case 'check_deposit': return 'primary';
    case 'incoming_domestic_wire':
    case 'incoming_international_wire':
    case 'exogenous_wire_drawdown':
    case 'currency_cloud_return': return 'info';
    case 'outgoing_payment':
    case 'expense_reimbursement': return 'success';
    case 'wire_fee':
    case 'card_international_transaction_fee':
    case 'card_international_transaction_fee_reversal':
    case 'personal_banking_subscription_fee':
    case 'billing_engine_subscription_fee': return 'danger';
    case 'card_international_transaction_fee_rebate':
    case 'card_international_transaction_fee_rebate_reversal': return 'warning';
    default: return 'secondary';
  }
};

// methodLabel: BankTransaction.method (rail) — ach/check/wire/swift/card/etc.
export const methodLabel = (m?: string): string => {
  switch (m) {
    case 'ach': return 'ACH';
    case 'check': return 'Check';
    case 'wire': return 'Wire';
    case 'swift': return 'SWIFT Wire';
    case 'debit_card': return 'Debit Card';
    case 'credit_card': return 'Credit Card';
    case 'bank_option': return 'Bank Option';
    case 'non_payment': return 'Non-Payment';
    default: return m || '';
  }
};

export const paymentFormatLabel = (f?: string): string => {
  switch (f) {
    case 'ccd': return 'Cash Concentration/Disbursement';
    case 'ccd_plus': return 'Cash Concentration/Disbursement (with Addenda)';
    case 'corporate_trade': return 'Corporate Trade Exchange';
    case 'prearranged_entry': return 'Prearranged Payment and Deposit';
    case 'prearranged_plus': return 'Prearranged Payment and Deposit (with Addenda)';
    case 'international_ach': return 'International ACH';
    default: return f || '';
  }
};

export const methodOptions = [
  { value: 'ach', label: 'ACH' },
  { value: 'check', label: 'Check' },
  { value: 'wire', label: 'Wire' },
  { value: 'swift', label: 'SWIFT Wire' },
  { value: 'debit_card', label: 'Debit Card' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'bank_option', label: 'Bank Option' },
  { value: 'non_payment', label: 'Non-Payment' },
];

export const paymentFormatOptions = [
  { value: 'ccd', label: 'Cash Concentration/Disbursement' },
  { value: 'ccd_plus', label: 'Cash Concentration/Disbursement (with Addenda)' },
  { value: 'corporate_trade', label: 'Corporate Trade Exchange' },
  { value: 'prearranged_entry', label: 'Prearranged Payment and Deposit' },
  { value: 'prearranged_plus', label: 'Prearranged Payment and Deposit (with Addenda)' },
  { value: 'international_ach', label: 'International ACH' },
];
